/**
 * The admin's face: who is allowed in, and the comparison.
 *
 * The comparison is the product. Everything else in this file exists to get a
 * row onto that screen.
 */
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api } from '../api/client'
import {
  AttachmentsPanel,
  Button,
  CatalogFabricPanel,
  DeliveryProgress,
  Empty,
  Field,
  Loading,
  Pill,
  Problem,
  ShareInvite,
  Tag,
  leadTime,
  measurement,
  shortDate,
} from '../ui'
import { RequirementCard, type RequirementView } from './Buyer'

interface Organization {
  id: string
  kind: 'BUYER' | 'COMPANY'
  name: string
  status: string
  contactName: string | null
  phone: string | null
  city: string | null
  createdAt: string
}

interface ComparisonRow {
  /** The enquiry's id, not the reply's — see `EnquiriesService.forRequirement`. */
  id: string
  company: { id: string; name: string; city: string | null }
  sentAt: string
  dueAt: string | null
  reply: {
    id: string
    answer: string
    price: string | null
    currency: string | null
    leadTimeDays: number | null
    minQuantity: string | null
    reason: string | null
    comments: string | null
  } | null
}

/* -------------------------------------------------------------- dashboard */

interface DashboardSummary {
  pipeline: Record<string, number>
  pendingOrganizations: number
  unansweredEnquiries: number
  atRisk: {
    id: string
    reference: string
    fabricDescription: string
    daysLeft: number
    overdue: boolean
  }[]
}

interface NotificationRow {
  id: string
  kind: string
  title: string
  body: string | null
  link: string | null
  createdAt: string
}

const PIPELINE_LABELS: Record<string, string> = {
  SUBMITTED: 'Submitted',
  REJECTED: 'Rejected',
  ENQUIRED: 'Enquired',
  QUOTED: 'Quoted',
  IN_PROGRESS: 'In progress',
  CLOSED: 'Closed',
}

export function AdminDashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [activity, setActivity] = useState<NotificationRow[] | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [dashboard, notifications] = await Promise.all([
          api.get<DashboardSummary>('/dashboard'),
          api.get<{ notifications: NotificationRow[] }>('/notifications?limit=8'),
        ])
        setSummary(dashboard)
        setActivity(notifications.notifications)
      } catch (problem) {
        setError(problem)
      }
    })()
  }, [])

  if (!summary && !error) return <Loading />

  return (
    <>
      <h1>Dashboard</h1>
      <p className="lede">Where everything stands, at a glance.</p>
      <Problem error={error} />

      {summary ? (
        <>
          <div className="stat-grid">
            <Link className="stat-tile" to="/organizations">
              <div className="stat-tile-value">{summary.pendingOrganizations}</div>
              <div className="stat-tile-label">Waiting for approval</div>
            </Link>
            <Link className="stat-tile" to="/requirements">
              <div className="stat-tile-value">{summary.pipeline.SUBMITTED ?? 0}</div>
              <div className="stat-tile-label">Need a decision</div>
            </Link>
            <Link className="stat-tile" to="/requirements">
              <div className="stat-tile-value">{summary.unansweredEnquiries}</div>
              <div className="stat-tile-label">Unanswered enquiries</div>
            </Link>
            <Link className="stat-tile" to="/requirements">
              <div className="stat-tile-value">{summary.atRisk.length}</div>
              <div className="stat-tile-label">Orders at risk</div>
            </Link>
          </div>

          <h2>Pipeline</h2>
          <div className="card">
            <ul className="specs">
              {Object.entries(PIPELINE_LABELS).map(([key, label]) => (
                <li key={key}>
                  {label} <b>{summary.pipeline[key] ?? 0}</b>
                </li>
              ))}
            </ul>
          </div>

          {summary.atRisk.length > 0 ? (
            <>
              <h2>Orders at risk</h2>
              {summary.atRisk.map((order) => (
                <Link className="card card-link" to={`/requirements/${order.id}`} key={order.id}>
                  <div className="card-head">
                    <strong>{order.fabricDescription}</strong>
                    <Pill
                      value={order.overdue ? 'REJECTED' : 'PENDING'}
                      label={
                        order.overdue
                          ? `Overdue by ${Math.abs(order.daysLeft)} ${Math.abs(order.daysLeft) === 1 ? 'day' : 'days'}`
                          : order.daysLeft === 0
                            ? 'Due today'
                            : `${order.daysLeft} ${order.daysLeft === 1 ? 'day' : 'days'} left`
                      }
                    />
                  </div>
                  <div className="reference">{order.reference}</div>
                </Link>
              ))}
            </>
          ) : null}

          <h2>Recent activity</h2>
          {activity?.length === 0 ? <Empty>Nothing yet.</Empty> : null}
          {activity?.map((row) => (
            <div className="card" key={row.id}>
              <div className="card-head">
                <strong>{row.title}</strong>
              </div>
              <div className="reference">{shortDate(row.createdAt)}</div>
              {row.body ? <p className="field-hint">{row.body}</p> : null}
            </div>
          ))}
        </>
      ) : null}

      <AdminQueue />
    </>
  )
}

/* ------------------------------------------------------------------ queue */

export function AdminQueue() {
  const [waiting, setWaiting] = useState<Organization[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = async () => {
    try {
      const result = await api.get<{ organizations: Organization[] }>(
        '/organizations?status=PENDING',
      )
      setWaiting(result.organizations)
    } catch (problem) {
      setError(problem)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const decide = async (id: string, decision: 'approve' | 'reject') => {
    setBusyId(id)
    setError(null)
    try {
      await api.post(`/organizations/${id}/${decision}`)
      await load()
    } catch (problem) {
      setError(problem)
    } finally {
      setBusyId(null)
    }
  }

  if (!waiting && !error) return <Loading />

  return (
    <>
      <h2>Waiting for you</h2>
      <p className="lede">
        Nobody here can sign in, and no enquiry can reach them, until you decide.
      </p>
      <Problem error={error} />

      {waiting?.length === 0 ? <Empty>Nobody is waiting.</Empty> : null}

      {waiting?.map((organization) => (
        <div className="card" key={organization.id}>
          <div className="card-head">
            <strong>{organization.name}</strong>
            <Tag>{organization.kind === 'BUYER' ? 'Buyer' : 'Company'}</Tag>
          </div>
          <div className="reference">
            {organization.contactName ?? '—'}
            {organization.phone ? ` · ${organization.phone}` : ''} · registered{' '}
            {shortDate(organization.createdAt)}
          </div>
          <div className="actions">
            <Button
              busy={busyId === organization.id}
              onClick={() => decide(organization.id, 'approve')}
            >
              Approve
            </Button>
            <Button
              variant="danger"
              busy={busyId === organization.id}
              onClick={() => decide(organization.id, 'reject')}
            >
              Turn away
            </Button>
          </div>
        </div>
      ))}
    </>
  )
}

/* ----------------------------------------------------------- requirements */

type AdminRequirementFilter = 'ALL' | 'SUBMITTED' | 'REJECTED' | 'ENQUIRED' | 'QUOTED' | 'IN_PROGRESS' | 'CLOSED'

export function AdminRequirements() {
  const [requirements, setRequirements] = useState<RequirementView[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [filter, setFilter] = useState<AdminRequirementFilter>('ALL')
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        const result = await api.get<{ requirements: RequirementView[] }>('/requirements')
        setRequirements(result.requirements)
      } catch (problem) {
        setError(problem)
      }
    })()
  }, [])

  if (!requirements && !error) return <Loading />

  // DRAFT never appears here at all — a draft is invisible to the admin.
  const statuses: { key: AdminRequirementFilter; label: string }[] = [
    { key: 'SUBMITTED', label: 'Submitted' },
    { key: 'REJECTED', label: 'Rejected' },
    { key: 'ENQUIRED', label: 'Enquired' },
    { key: 'QUOTED', label: 'Quoted' },
    { key: 'IN_PROGRESS', label: 'In progress' },
    { key: 'CLOSED', label: 'Closed' },
  ]
  const sections = statuses.map((section) => ({
    ...section,
    items: requirements?.filter((r) => r.status === section.key) ?? [],
  }))
  const shown = filter === 'ALL' ? requirements ?? [] : sections.find((s) => s.key === filter)?.items ?? []
  const activeLabel = sections.find((s) => s.key === filter)?.label

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h1>Requirements</h1>
        <Button variant="quiet" onClick={() => setDrawerOpen(true)}>
          {activeLabel ?? 'All'} ▾
        </Button>
      </div>
      <p className="lede">Everything the buyers have sent.</p>
      <Problem error={error} />

      {drawerOpen ? (
        <>
          <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />
          <nav className="drawer">
            <div className="drawer-head">
              <strong>Show</strong>
              <Button variant="quiet" onClick={() => setDrawerOpen(false)}>
                Close
              </Button>
            </div>
            <button
              className={`drawer-item${filter === 'ALL' ? ' drawer-item-active' : ''}`}
              onClick={() => {
                setFilter('ALL')
                setDrawerOpen(false)
              }}
            >
              <span>All</span>
              <span className="drawer-count">{requirements?.length ?? 0}</span>
            </button>
            {sections.map((section) => (
              <button
                key={section.key}
                className={`drawer-item${filter === section.key ? ' drawer-item-active' : ''}`}
                onClick={() => {
                  setFilter(section.key)
                  setDrawerOpen(false)
                }}
              >
                <span>{section.label}</span>
                <span className="drawer-count">{section.items.length}</span>
              </button>
            ))}
          </nav>
        </>
      ) : null}

      {requirements?.length === 0 ? <Empty>Nothing has been submitted yet.</Empty> : null}
      {requirements && requirements.length > 0 && shown.length === 0 ? (
        <Empty>Nothing here.</Empty>
      ) : null}
      {shown.map((requirement) => (
        <RequirementCard
          key={requirement.id}
          requirement={requirement}
          href={`/requirements/${requirement.id}`}
        />
      ))}
    </>
  )
}

/* ------------------------------------------------------------ comparison */

export function AdminRequirementDetail() {
  const { id = '' } = useParams()
  const [requirement, setRequirement] = useState<RequirementView | null>(null)
  const [rows, setRows] = useState<ComparisonRow[] | null>(null)
  const [companies, setCompanies] = useState<Organization[]>([])
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [quoteOpenFor, setQuoteOpenFor] = useState<string | null>(null)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectNote, setRejectNote] = useState('')
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false)

  const load = async () => {
    try {
      const [detail, comparison, picker] = await Promise.all([
        api.get<{ requirement: RequirementView }>(`/requirements/${id}`),
        api.get<{ enquiries: ComparisonRow[] }>(`/requirements/${id}/enquiries`),
        api.get<{ organizations: Organization[] }>(
          '/organizations?kind=COMPANY&status=APPROVED',
        ),
      ])
      setRequirement(detail.requirement)
      setRows(comparison.enquiries)
      setCompanies(picker.organizations)
    } catch (problem) {
      setError(problem)
    }
  }

  useEffect(() => {
    void load()
  }, [id])

  const ask = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/requirements/${id}/enquiries`, { companyIds: [...chosen] })
      setChosen(new Set())
      await load()
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  const close = async () => {
    setBusy(true)
    try {
      await api.post(`/requirements/${id}/close`)
      setCloseConfirmOpen(false)
      await load()
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  const reject = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/requirements/${id}/reject`, {
        note: rejectNote.trim() === '' ? null : rejectNote.trim(),
      })
      setRejectOpen(false)
      setRejectNote('')
      await load()
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  if (!requirement && !error) return <Loading />

  const alreadyAsked = new Set(rows?.map((row) => row.company.id) ?? [])
  const askable = companies.filter((company) => !alreadyAsked.has(company.id))

  return (
    <>
      <Link to="/requirements" className="reference">
        ← All requirements
      </Link>
      <h1 style={{ marginTop: 8 }}>{requirement?.fabricDescription}</h1>
      <Problem error={error} />

      {requirement ? <RequirementCard requirement={requirement} /> : null}

      {requirement?.catalogFabric ? <CatalogFabricPanel fabric={requirement.catalogFabric} /> : null}

      {requirement ? <AttachmentsPanel kind="requirement" id={requirement.id} canUpload /> : null}

      {requirement?.status === 'REJECTED' ? (
        <div className="card">
          <div className="card-head">
            <strong>Declined</strong>
          </div>
          {requirement.rejectionNote ? (
            <p className="field-hint">{requirement.rejectionNote}</p>
          ) : (
            <p className="field-hint">No note was given.</p>
          )}
        </div>
      ) : null}

      {requirement?.status === 'SUBMITTED' ? (
        <div className="card">
          <div className="card-head">
            <strong>Not yet asked</strong>
          </div>
          {rejectOpen ? (
            <>
              <Field
                label="Note to the buyer (optional)"
                hint="Why this isn't going forward — shown on their screen."
              >
                <textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} />
              </Field>
              <div className="actions">
                <Button variant="danger" busy={busy} onClick={reject}>
                  Confirm decline
                </Button>
                <Button
                  variant="quiet"
                  onClick={() => {
                    setRejectOpen(false)
                    setRejectNote('')
                  }}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div className="actions">
              <Button variant="quiet" onClick={() => setRejectOpen(true)}>
                Decline this requirement
              </Button>
            </div>
          )}
        </div>
      ) : null}

      {requirement?.status === 'QUOTED' || requirement?.status === 'IN_PROGRESS' ? (
        <div className="card">
          <div className="card-head">
            <strong>
              {requirement.status === 'IN_PROGRESS' ? 'Buyer confirmed' : 'Waiting on the buyer'}
            </strong>
          </div>
          <ul className="specs">
            <li>
              Quoted{' '}
              <b>
                {measurement(requirement.quotedPrice ?? null)} {requirement.quotedCurrency ?? ''}
              </b>
            </li>
            <li>
              Lead time <b>{leadTime(requirement.quotedLeadTimeDays ?? null)}</b>
            </li>
          </ul>
          {requirement.status === 'IN_PROGRESS' ? (
            <DeliveryProgress
              confirmedAt={requirement.confirmedAt}
              leadTimeDays={requirement.quotedLeadTimeDays}
            />
          ) : null}
        </div>
      ) : null}

      <h2>Answers</h2>
      {rows?.length === 0 ? <Empty>Nobody has been asked yet.</Empty> : null}
      {rows?.map((row) => {
        const isChosen = row.reply != null && requirement?.chosenReplyId === row.reply.id
        // Once the buyer has confirmed (or the admin closed this outright),
        // a mill that never answered is not "waiting" for anything anymore —
        // there is nothing left to wait for. Same fix as the mill's own inbox.
        const locked = requirement?.status === 'IN_PROGRESS' || requirement?.status === 'CLOSED'
        const canQuote =
          row.reply != null &&
          row.reply.answer !== 'NO' &&
          (requirement?.status === 'ENQUIRED' || requirement?.status === 'QUOTED')

        return (
          <div className="card" key={row.id}>
            <div className="card-head">
              <strong>{row.company.name}</strong>
              <span style={{ display: 'flex', gap: 6 }}>
                {row.reply ? (
                  <Pill value={row.reply.answer} />
                ) : locked ? (
                  <Pill value="REJECTED" label="Closed" />
                ) : (
                  <Pill value="WAITING" />
                )}
                {/* `confirmedAt`, not `status === 'IN_PROGRESS'`: closing a
                    confirmed order afterwards must not erase who won it. */}
                {isChosen && requirement?.confirmedAt ? (
                  <Pill value="APPROVED" label="Order confirmed" />
                ) : null}
              </span>
            </div>
            {row.reply ? (
              <ul className="specs">
                <li>
                  Price{' '}
                  <b>
                    {row.reply.price ?? '—'} {row.reply.currency ?? ''}
                  </b>
                </li>
                <li>
                  Lead time <b>{leadTime(row.reply.leadTimeDays)}</b>
                </li>
                <li>
                  Minimum <b>{measurement(row.reply.minQuantity)}</b>
                </li>
                {row.reply.reason ? (
                  <li>
                    Reason <b>{row.reply.reason.replace(/_/g, ' ').toLowerCase()}</b>
                  </li>
                ) : null}
              </ul>
            ) : (
              <div className="reference">
                asked {shortDate(row.sentAt)} ·{' '}
                {locked ? 'never answered — this is closed' : 'no answer yet'}
              </div>
            )}
            {row.reply?.comments ? <p className="field-hint">{row.reply.comments}</p> : null}

            <AttachmentsPanel kind="enquiry" id={row.id} />

            {canQuote && quoteOpenFor !== row.id ? (
              <div className="actions">
                <Button variant={isChosen ? 'quiet' : 'primary'} onClick={() => setQuoteOpenFor(row.id)}>
                  {isChosen ? 'Sent to buyer · change' : 'Send this price to the buyer'}
                </Button>
              </div>
            ) : null}

            {canQuote && quoteOpenFor === row.id && row.reply ? (
              <QuoteForm
                requirementId={id}
                replyId={row.reply.id}
                reply={row.reply}
                onCancel={() => setQuoteOpenFor(null)}
                onDone={async () => {
                  setQuoteOpenFor(null)
                  await load()
                }}
              />
            ) : null}
          </div>
        )
      })}

      {requirement?.status !== 'CLOSED' &&
      requirement?.status !== 'IN_PROGRESS' &&
      requirement?.status !== 'REJECTED' ? (
        <>
          <h2>Ask more companies</h2>
          {requirement?.status === 'DRAFT' ? (
            <Empty>The buyer has not sent this yet.</Empty>
          ) : askable.length === 0 ? (
            <Empty>Everyone approved has already been asked.</Empty>
          ) : (
            <div className="pickers">
              {askable.map((company) => (
                <label className="picker" key={company.id}>
                  <input
                    type="checkbox"
                    checked={chosen.has(company.id)}
                    onChange={(event) =>
                      setChosen((current) => {
                        const next = new Set(current)
                        if (event.target.checked) next.add(company.id)
                        else next.delete(company.id)
                        return next
                      })
                    }
                  />
                  <span>
                    {company.name}
                    {company.city ? ` · ${company.city}` : ''}
                  </span>
                </label>
              ))}
            </div>
          )}
        </>
      ) : null}

      {requirement?.status !== 'DRAFT' &&
      requirement?.status !== 'IN_PROGRESS' &&
      requirement?.status !== 'CLOSED' &&
      requirement?.status !== 'REJECTED' &&
      askable.length > 0 ? (
        <div className="actions">
          <Button busy={busy} disabled={chosen.size === 0} onClick={ask}>
            Ask {chosen.size > 0 ? `${chosen.size} ` : ''}
            {chosen.size === 1 ? 'company' : 'companies'}
          </Button>
        </div>
      ) : null}

      {/* Closing stays available on its own — a confirmed order still has to
          be closable once it ships, even though asking anyone new about it
          no longer makes sense. Not offered once REJECTED: that is already
          its own terminal state, and closing on top of it would leave two
          different ways to say "dead" for one requirement.

          Deliberately its own card, physically apart from the routine
          actions above, with a confirm step (same reveal-then-act pattern
          as the reject flow) and a danger button — this is a one-way
          transition with no route back, and used to sit as an
          undifferentiated `quiet` button right after "Everyone approved has
          already been asked", which made it an easy accidental tap. */}
      {requirement?.status !== 'CLOSED' && requirement?.status !== 'REJECTED' ? (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-head">
            <strong>Close this requirement</strong>
          </div>
          <p className="field-hint">
            This cannot be undone — a closed requirement cannot be reopened, asked about, or
            quoted again.
          </p>
          {closeConfirmOpen ? (
            <div className="actions">
              <Button variant="danger" busy={busy} onClick={close}>
                Yes, close it
              </Button>
              <Button variant="quiet" onClick={() => setCloseConfirmOpen(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="actions">
              <Button variant="danger" onClick={() => setCloseConfirmOpen(true)}>
                Close this requirement
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </>
  )
}

/**
 * Turning one mill's answer into a price the buyer sees.
 *
 * Prefilled from the reply, but every field is editable — the admin is a
 * broker, not a pass-through, and is free to mark the price up before it
 * reaches the buyer. The buyer never learns which mill this came from, so the
 * note field is the wrong place to name one.
 */
function QuoteForm({
  requirementId,
  replyId,
  reply,
  onDone,
  onCancel,
}: {
  requirementId: string
  replyId: string
  reply: ComparisonRow['reply']
  onDone: () => void
  onCancel: () => void
}) {
  const [price, setPrice] = useState(reply?.price ?? '')
  const [currency, setCurrency] = useState(reply?.currency ?? 'INR')
  const [leadTimeDays, setLeadTimeDays] = useState(
    reply?.leadTimeDays != null ? String(reply.leadTimeDays) : '',
  )
  const [note, setNote] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post(`/requirements/${requirementId}/quote`, {
        replyId,
        price,
        currency,
        leadTimeDays: leadTimeDays.trim() === '' ? null : leadTimeDays,
        note: note.trim() === '' ? null : note.trim(),
      })
      onDone()
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 12 }}>
      <Problem error={error} />
      <div className="row">
        <Field label="Price to quote the buyer">
          <input
            inputMode="decimal"
            required
            value={price ?? ''}
            onChange={(e) => setPrice(e.target.value)}
          />
        </Field>
        <Field label="Currency">
          <input
            required
            maxLength={3}
            value={currency ?? ''}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          />
        </Field>
      </div>
      <Field label="Lead time (days)">
        <input
          inputMode="numeric"
          value={leadTimeDays}
          onChange={(e) => setLeadTimeDays(e.target.value)}
        />
      </Field>
      <Field label="Note to the buyer" hint="Optional. Never names the mill.">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <div className="actions">
        <Button type="submit" busy={busy}>
          Send this price to the buyer
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

/* --------------------------------------------------------- organizations */

type OrganizationFilter = 'ALL' | 'BUYER' | 'COMPANY'

export function AdminOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [filter, setFilter] = useState<OrganizationFilter>('ALL')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [addingKind, setAddingKind] = useState<'BUYER' | 'COMPANY' | null>(null)

  const load = async () => {
    try {
      const result = await api.get<{ organizations: Organization[] }>('/organizations')
      setOrganizations(result.organizations)
    } catch (problem) {
      setError(problem)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  if (!organizations && !error) return <Loading />

  const kinds: { key: OrganizationFilter; label: string }[] = [
    { key: 'BUYER', label: 'Buyers' },
    { key: 'COMPANY', label: 'Companies' },
  ]
  const sections = kinds.map((section) => ({
    ...section,
    items: organizations?.filter((o) => o.kind === section.key) ?? [],
  }))
  const shown = filter === 'ALL' ? organizations ?? [] : sections.find((s) => s.key === filter)?.items ?? []
  const activeLabel = sections.find((s) => s.key === filter)?.label

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h1>Companies and buyers</h1>
        <Button variant="quiet" onClick={() => setDrawerOpen(true)}>
          {activeLabel ?? 'All'} ▾
        </Button>
      </div>
      <p className="lede">Everyone on the platform.</p>
      <Problem error={error} />

      <div className="actions">
        <Button
          variant="quiet"
          onClick={() => setAddingKind(addingKind === 'BUYER' ? null : 'BUYER')}
        >
          {addingKind === 'BUYER' ? 'Close' : 'Add a buyer'}
        </Button>
        <Button
          variant="quiet"
          onClick={() => setAddingKind(addingKind === 'COMPANY' ? null : 'COMPANY')}
        >
          {addingKind === 'COMPANY' ? 'Close' : 'Add a company'}
        </Button>
      </div>
      {addingKind ? <CreateOrganization kind={addingKind} onDone={load} /> : null}

      {drawerOpen ? (
        <>
          <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />
          <nav className="drawer">
            <div className="drawer-head">
              <strong>Show</strong>
              <Button variant="quiet" onClick={() => setDrawerOpen(false)}>
                Close
              </Button>
            </div>
            <button
              className={`drawer-item${filter === 'ALL' ? ' drawer-item-active' : ''}`}
              onClick={() => {
                setFilter('ALL')
                setDrawerOpen(false)
              }}
            >
              <span>All</span>
              <span className="drawer-count">{organizations?.length ?? 0}</span>
            </button>
            {sections.map((section) => (
              <button
                key={section.key}
                className={`drawer-item${filter === section.key ? ' drawer-item-active' : ''}`}
                onClick={() => {
                  setFilter(section.key)
                  setDrawerOpen(false)
                }}
              >
                <span>{section.label}</span>
                <span className="drawer-count">{section.items.length}</span>
              </button>
            ))}
          </nav>
        </>
      ) : null}

      {organizations?.length === 0 ? <Empty>Nobody yet.</Empty> : null}
      {organizations && organizations.length > 0 && shown.length === 0 ? (
        <Empty>Nothing here.</Empty>
      ) : null}

      {shown.map((organization) => (
        <div className="card" key={organization.id}>
          <div className="card-head">
            <strong>{organization.name}</strong>
            <Pill value={organization.status} />
          </div>
          <div className="reference">
            {organization.kind.toLowerCase()}
            {organization.city ? ` · ${organization.city}` : ''}
          </div>
          <div className="actions">
            <Link className="button button-quiet" to={`/organizations/${organization.id}`}>
              {organization.kind === 'BUYER' ? 'Follow requirements' : 'Follow track record'}
            </Link>
            <Button
              variant="quiet"
              onClick={() => setOpenId(openId === organization.id ? null : organization.id)}
            >
              {openId === organization.id ? 'Close' : 'Add a person'}
            </Button>
          </div>
          {openId === organization.id ? (
            <AddPerson
              organizationId={organization.id}
              organizationPhone={organization.phone}
              onDone={load}
            />
          ) : null}
        </div>
      ))}
    </>
  )
}

/**
 * Adding a colleague to a mill.
 *
 * The invitation link is offered straight to WhatsApp rather than only
 * shown, because there is no mail transport yet: the admin passes it on over
 * WhatsApp, which is how this client already works — see `ShareInvite`.
 */
function AddPerson({
  organizationId,
  organizationPhone,
  onDone,
}: {
  organizationId: string
  organizationPhone: string | null
  onDone: () => void
}) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [invite, setInvite] = useState<{ url: string; email: string } | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api.post<{ invite: { url: string } }>(
        `/organizations/${organizationId}/users`,
        { email, displayName },
      )
      // Captured before the fields clear below -- `invite` needs it for the
      // WhatsApp message, since nothing else on the set-password screen ever
      // tells the person what their login email is.
      setInvite({ url: result.invite.url, email })
      setEmail('')
      setDisplayName('')
      onDone()
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 12 }}>
      <Problem error={error} />
      <Field label="Their name">
        <input required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </Field>
      <Field label="Their email">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <div className="actions">
        <Button type="submit" busy={busy}>
          Add and make a link
        </Button>
      </div>
      {invite ? (
        <>
          <p className="field-hint" style={{ marginTop: 12 }}>
            Send them this link. It works once, and expires in three days.
          </p>
          <ShareInvite
            url={invite.url}
            phone={organizationPhone}
            message={`Your bjsfabric login is ${invite.email}. Set your password to get started:`}
          />
        </>
      ) : null}
    </form>
  )
}

/**
 * The same set-password link as `AddPerson`, for someone who already has an
 * account and is locked out of it — there is no mail transport, so this
 * replaces "forgot password" for this client: the person messages the admin
 * directly, and the admin sends this back over WhatsApp instead.
 * `AuthService.issuePasswordSetupToken` is one mechanism for both cases (see
 * its own comment), and issuing again supersedes whatever link was sent
 * before.
 */
function SendPasswordLink({
  organizationId,
  userId,
  email,
  organizationPhone,
}: {
  organizationId: string
  userId: string
  email: string
  organizationPhone: string | null
}) {
  const [invite, setInvite] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const send = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.post<{ invite: { url: string } }>(
        `/organizations/${organizationId}/users/${userId}/invite`,
      )
      setInvite(result.invite.url)
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginTop: 6 }}>
      <Problem error={error} />
      {invite ? (
        <ShareInvite
          url={invite}
          phone={organizationPhone}
          message={`Your bjsfabric login is ${email}. Here's a link to set a new password:`}
        />
      ) : (
        <Button type="button" variant="quiet" busy={busy} onClick={() => void send()}>
          Send password link
        </Button>
      )}
    </div>
  )
}

/**
 * The admin onboarding a company or buyer directly — the mills and buyers he
 * already knows, rather than waiting for one to register itself.
 *
 * Fields mirror self-registration's, because it is the same information —
 * typed in by the admin instead of by the organisation. Born approved (see
 * the API), so unlike a self-registered mill there is no separate approval
 * step: the admin adding it himself already is that decision.
 */
function CreateOrganization({
  kind,
  onDone,
}: {
  kind: 'BUYER' | 'COMPANY'
  onDone: () => void
}) {
  const [name, setName] = useState('')
  const [contactName, setContactName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [city, setCity] = useState('')
  const [invite, setInvite] = useState<{ url: string; phone: string | null } | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api.post<{ invite: { url: string } }>('/organizations', {
        kind,
        name,
        contactName,
        email,
        phone: phone || undefined,
        city: city || undefined,
      })
      setInvite({ url: result.invite.url, phone: phone || null })
      setName('')
      setContactName('')
      setEmail('')
      setPhone('')
      setCity('')
      onDone()
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <form onSubmit={submit}>
        <Problem error={error} />
        <Field label={kind === 'BUYER' ? "Buyer's company name" : "Mill's name"}>
          <input required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Contact person">
          <input required value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </Field>
        <div className="row">
          <Field label="Their email">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Phone" hint="For the WhatsApp share below">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
        <Field label="City" hint="Optional">
          <input value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Add and make a link
          </Button>
        </div>
      </form>
      {invite ? (
        <>
          <p className="field-hint" style={{ marginTop: 12 }}>
            Send them this link. It works once, and expires in three days.
          </p>
          <ShareInvite
            url={invite.url}
            phone={invite.phone}
            message={`You've been added to bjsfabric as ${
              kind === 'BUYER' ? 'a buyer' : 'a company'
            }. Set your password to get started:`}
          />
        </>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------- one organisation */

interface OrganizationDetail extends Organization {
  notes: string | null
  approvedAt: string | null
}

interface OrganizationUser {
  id: string
  email: string
  displayName: string
  role: string
  status: string
  lastSignedInAt: string | null
  createdAt: string
}

interface TrackRow {
  id: string
  sentAt: string
  dueAt: string | null
  adminNote: string | null
  requirement: {
    id: string
    reference: string
    status: string
    fabricDescription: string
    buyerName: string
  }
  reply: {
    answer: string
    price: string | null
    currency: string | null
    leadTimeDays: number | null
    reason: string | null
  } | null
  won: boolean
}

/**
 * One organisation, followed: a buyer's whole run of requirements, or a
 * mill's whole track record of enquiries — whichever it is.
 */
export function AdminOrganizationDetail() {
  const { id = '' } = useParams()
  const [organization, setOrganization] = useState<OrganizationDetail | null>(null)
  const [people, setPeople] = useState<OrganizationUser[]>([])
  const [requirements, setRequirements] = useState<RequirementView[] | null>(null)
  const [track, setTrack] = useState<TrackRow[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [editingOrg, setEditingOrg] = useState(false)
  const [editingPersonId, setEditingPersonId] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const detail = await api.get<{ organization: OrganizationDetail; users: OrganizationUser[] }>(
          `/organizations/${id}`,
        )
        setOrganization(detail.organization)
        setPeople(detail.users)

        if (detail.organization.kind === 'BUYER') {
          const result = await api.get<{ requirements: RequirementView[] }>(
            `/requirements?buyerId=${id}&limit=100`,
          )
          setRequirements(result.requirements)
        } else {
          const result = await api.get<{ enquiries: TrackRow[] }>(`/organizations/${id}/track`)
          setTrack(result.enquiries)
        }
      } catch (problem) {
        setError(problem)
      }
    })()
  }, [id])

  if (!organization && !error) return <Loading />

  return (
    <>
      <Link to="/organizations" className="reference">
        ← Companies and buyers
      </Link>
      <h1 style={{ marginTop: 8 }}>{organization?.name}</h1>
      <Problem error={error} />

      {organization ? (
        <div className="card">
          <div className="card-head">
            <strong>{organization.kind === 'BUYER' ? 'Buyer' : 'Company'}</strong>
            <Pill value={organization.status} />
          </div>
          {editingOrg ? (
            <EditOrganization
              organization={organization}
              onCancel={() => setEditingOrg(false)}
              onDone={(updated) => {
                setOrganization(updated)
                setEditingOrg(false)
              }}
            />
          ) : (
            <>
              <div className="reference">
                {organization.contactName ?? '—'}
                {organization.phone ? ` · ${organization.phone}` : ''}
                {organization.city ? ` · ${organization.city}` : ''}
              </div>
              <div className="actions">
                <Button variant="quiet" onClick={() => setEditingOrg(true)}>
                  Edit details
                </Button>
              </div>
            </>
          )}
          {people.length > 0 ? (
            <ul className="attachment-list" style={{ marginTop: 10 }}>
              {people.map((person) => (
                <li key={person.id}>
                  <div className="card-head" style={{ marginBottom: 0 }}>
                    <span>
                      {person.displayName} <span className="field-hint">{person.email}</span>
                    </span>
                    <Pill value={person.status} />
                  </div>
                  {editingPersonId === person.id ? (
                    <EditPerson
                      person={person}
                      onCancel={() => setEditingPersonId(null)}
                      onDone={(updated) => {
                        setPeople((current) =>
                          current.map((row) => (row.id === updated.id ? updated : row)),
                        )
                        setEditingPersonId(null)
                      }}
                    />
                  ) : (
                    <>
                      <SendPasswordLink
                        organizationId={id}
                        userId={person.id}
                        email={person.email}
                        organizationPhone={organization.phone}
                      />
                      <div className="actions">
                        <Button variant="quiet" onClick={() => setEditingPersonId(person.id)}>
                          Edit
                        </Button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {organization?.kind === 'BUYER' ? (
        <>
          <h2>Requirements</h2>
          {requirements?.length === 0 ? <Empty>Nothing sent yet.</Empty> : null}
          {requirements?.map((requirement) => (
            <RequirementCard
              key={requirement.id}
              requirement={requirement}
              href={`/requirements/${requirement.id}`}
            />
          ))}
        </>
      ) : null}

      {organization?.kind === 'COMPANY' ? (
        <>
          <h2>Track record</h2>
          {track?.length === 0 ? <Empty>Nothing has been sent to it yet.</Empty> : null}
          {track?.map((row) => (
            <Link className="card card-link" to={`/requirements/${row.requirement.id}`} key={row.id}>
              <div className="card-head">
                <strong>{row.requirement.fabricDescription}</strong>
                <span style={{ display: 'flex', gap: 6 }}>
                  {row.reply ? <Pill value={row.reply.answer} /> : <Pill value="WAITING" />}
                  {row.won ? <Pill value="APPROVED" label="Won" /> : null}
                </span>
              </div>
              <div className="reference">
                {row.requirement.reference} · {row.requirement.buyerName} · asked{' '}
                {shortDate(row.sentAt)}
              </div>
              {row.reply ? (
                <ul className="specs">
                  <li>
                    Price{' '}
                    <b>
                      {row.reply.price ?? '—'} {row.reply.currency ?? ''}
                    </b>
                  </li>
                  <li>
                    Lead time <b>{leadTime(row.reply.leadTimeDays)}</b>
                  </li>
                </ul>
              ) : null}
            </Link>
          ))}
        </>
      ) : null}
    </>
  )
}

/** Correcting an organisation's own details. Not `kind` — see the API. */
function EditOrganization({
  organization,
  onCancel,
  onDone,
}: {
  organization: OrganizationDetail
  onCancel: () => void
  onDone: (organization: OrganizationDetail) => void
}) {
  const [name, setName] = useState(organization.name)
  const [contactName, setContactName] = useState(organization.contactName ?? '')
  const [phone, setPhone] = useState(organization.phone ?? '')
  const [city, setCity] = useState(organization.city ?? '')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api.patch<{ organization: OrganizationDetail }>(
        `/organizations/${organization.id}`,
        { name, contactName, phone: phone || undefined, city: city || undefined },
      )
      onDone(result.organization)
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 12 }}>
      <Problem error={error} />
      <Field label={organization.kind === 'BUYER' ? "Buyer's company name" : "Mill's name"}>
        <input required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Contact person">
        <input required value={contactName} onChange={(e) => setContactName(e.target.value)} />
      </Field>
      <div className="row">
        <Field label="Phone" hint="For the WhatsApp share">
          <input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="City" hint="Optional">
          <input value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
      </div>
      <div className="actions">
        <Button type="submit" busy={busy}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

/**
 * Correcting a person's own details — most often a mistyped email caught
 * only once they try to sign in with it. Not `role` — see the API.
 */
function EditPerson({
  person,
  onCancel,
  onDone,
}: {
  person: OrganizationUser
  onCancel: () => void
  onDone: (user: OrganizationUser) => void
}) {
  const [displayName, setDisplayName] = useState(person.displayName)
  const [email, setEmail] = useState(person.email)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api.patch<{ user: OrganizationUser }>(`/users/${person.id}`, {
        displayName,
        email,
      })
      onDone(result.user)
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 8 }}>
      <Problem error={error} />
      <Field label="Name">
        <input required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </Field>
      <Field label="Email">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <div className="actions">
        <Button type="submit" busy={busy}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
